import type { m365FilesTr } from './m365-files.tr.js';

/** English twin of m365-files.tr.ts; the same keys. */
export const m365FilesEn: typeof m365FilesTr = {
  sources: { onedrive: 'OneDrive', sharepoint: 'SharePoint', outlook: 'Outlook attachments' },
  menu: {
    computer: 'Upload from computer',
    computerHint: 'PDF, Word, Excel, PowerPoint, image · up to {{mb}} MB',
    computerHintCode: 'Code, document, image or archive · up to {{mb}} MB',
    group: 'Microsoft 365',
    hints: {
      onedrive: 'My files and files shared with me',
      sharepoint: "The company's sites and document libraries",
      outlook: 'Files attached to mails',
    },
    skillHome: 'Pick one on the Skills page',
    skillConversation: 'Add a new skill to this chat',
    connection: 'Opens the Connections page',
  },
  picker: {
    title: 'Add documents from Microsoft 365',
    nav: 'Sources',
    sites: 'Sites',
    allSites: 'All sites…',
    crumbs: 'Location',
    search: {
      library: 'Search this library',
      folder: 'Search this folder',
      onedrive: 'Search OneDrive',
      sites: 'Search sites',
      outlook: 'Search attachments',
    },
    results: 'Results for “{{query}}”',
    clearSearch: 'Clear search',
    table: 'Files',
    columns: { name: 'Name', by: 'Modified by', at: 'Modified', size: 'Size', from: 'From', date: 'Date' },
    select: 'Select: {{name}}',
    items_one: '{{count}} item',
    items_other: '{{count}} items',
    shared: 'Shared',
    more: 'Show more',
    note: 'Only files you may see in Microsoft are listed. At most {{max}} files at a time.',
    limit: 'You can pick at most {{max}} files; let one go to pick another.',
    loading: 'Loading files…',
    empty: {
      folder: 'This folder is empty.',
      search: 'No file matches your search.',
      outlook: 'No attached files in recent mails.',
      sites: 'You follow no site. Search for a site by its name.',
      site: 'This site has no document library.',
    },
    failed: 'This could not be opened.',
    retry: 'Try again',
    none: 'No document picked',
    selected_one: '{{count}} document picked',
    selected_other: '{{count}} documents picked',
    addNone: 'Add',
    add_one: 'Add {{count}} document',
    add_other: 'Add {{count}} documents',
    copyNote: 'Documents are copied once. They say <b>Source: {{source}}</b>; if the source changes, update them with “Fetch again”.',
    copyNoteOutlook: 'Attachments are copied once. They say <b>Source: {{source}}</b>; mail attachments do not change.',
  },
  blocked: {
    unsupported: 'This file type cannot be read',
    too_large: 'Over the {{mb}} MB limit',
    malware: 'Microsoft found malware in this file',
    package: 'Packages such as notebooks cannot be added',
    source_off: 'Fetching documents from here is turned off',
    microsoft_scopes_required: 'New permission needed · reconnect',
    empty: 'The file is empty',
  },
  targets: {
    legend: 'Where should they go?',
    legendSave: 'Where should it be saved?',
    user: 'Personal',
    userHint: 'Only you see them · default',
    team: 'Team: {{name}}',
    teamHint: 'Everyone in your team sees them',
    teamHintSave: 'Everyone in your team sees it · a warning comes when picked',
    workspace: 'Workspace: {{name}}',
    workspaceHint: 'Everyone in the workspace sees them',
    organization: 'Everyone',
    organizationHint: 'Everyone in the company sees them',
    conversation: 'This chat',
    conversationHint: 'Used in this chat only',
    locked: 'Everyone: super admins only',
    warning: {
      team: 'Everyone in the {{name}} team sees them, {{tail}}',
      workspace: 'Everyone in the {{name}} workspace sees them, {{tail}}',
      organization: 'Everyone in the company sees them, {{tail}}',
    },
    tail: {
      sharepoint: 'also people who cannot see these files in SharePoint.',
      onedrive: 'also people who cannot see these files in OneDrive.',
      outlook: 'also people who did not get this mail.',
      mixed: 'also people who cannot see these files in Microsoft 365.',
    },
    acknowledge: 'I understand',
  },
  access: {
    connect: { title: 'Connect your Microsoft account for this', text: 'Once connected, your files are listed here.', action: 'Connect my account' },
    reconnect: { title: 'You need to renew your Microsoft connection', text: 'Your Microsoft account no longer allows Oppy. It goes on once you reconnect.', action: 'Reconnect' },
    consent: { title: "Waiting for your company's consent", text: "Microsoft wants your company's admin to consent to these permissions. Tell your IT team." },
    scopes: {
      title: 'New permission needed · reconnect',
      text: {
        onedrive: 'Connect to your Microsoft account once more to see your OneDrive files; Microsoft will ask you for the new permission.',
        sharepoint: 'Connect to your Microsoft account once more to see the files of SharePoint sites; Microsoft will ask you for the new permission.',
        outlook: 'Connect to your Microsoft account once more to see mail attachments; Microsoft will ask you for the new permission.',
      },
      action: 'Reconnect',
    },
    off: {
      title: { onedrive: 'Fetching documents from OneDrive is turned off', sharepoint: 'Fetching documents from SharePoint is turned off', outlook: 'Reading mail is turned off' },
      text: 'Your company or your team turned this off. Documents you fetched stay in Documents.',
    },
    unavailable: { title: 'Microsoft cannot be reached right now', text: 'Try again in a little while.', action: 'Try again' },
    returned: 'Could not connect: {{reason}}',
    device: {
      intro: "Type this code on Microsoft's page; once you are signed in, your files are listed here.",
      open: "Open Microsoft's page",
      waiting: 'Waiting for your sign-in…',
      expired: 'The code expired.',
      declined: 'The sign-in was declined.',
      again: 'Get a new code',
    },
    badUrl: "Microsoft's page could not be opened; tell your admin.",
  },
  result: {
    failed_one: '{{count}} file could not be added',
    failed_other: '{{count}} files could not be added',
    unnamed: 'A file',
    others_one: 'The other {{count}} file was added.',
    others_other: 'The other {{count}} files were added.',
    duplicates_one: '{{count}} file was already in {{place}}.',
    duplicates_other: '{{count}} files were already in {{place}}.',
    added_one: '{{count}} document added · {{place}}',
    added_other: '{{count}} documents added · {{place}}',
    linkedHome: 'It is also used in the chat once it starts.',
    linked: 'Also linked to this chat.',
    ok: 'OK',
    reasons: {
      forbidden: 'you may no longer see this file.',
      not_found: 'the file was not found in Microsoft; it may have been deleted or moved.',
      too_large: 'it is over the {{mb}} MB limit.',
      unsupported: 'this file type cannot be read.',
      malware: 'Microsoft found malware in this file.',
      not_a_file: 'this is not a file.',
      empty: 'the file is empty.',
      source_off: 'fetching documents from Microsoft 365 is turned off.',
      microsoft_scopes_required: 'a new permission is needed; reconnect your Microsoft account.',
      unavailable: 'Microsoft cannot be reached right now; try again later.',
      busy: 'Microsoft cannot be reached right now; try again later.',
    },
  },
  save: {
    button: 'Save to my documents',
    saving: 'Saving…',
    saved: 'Saved · {{place}}',
    already: 'Already in Documents · {{place}}',
    failed: 'Could not save: {{reason}}',
    noteLead: 'In your documents it says',
    noteLabel: 'Source: Outlook · {{sender}}',
    noteLabelNoSender: 'Source: Outlook',
    noteTail: '. It can be used in document search and chats.',
    submit: 'Save',
    foot: 'The attachment was saved to your documents · {{place}}',
  },
};
